import React, { useRef, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material'
import TableEmptyState from './TableEmptyState'
import TableLoadingState from './TableLoadingState'
import styles from './BaseTable.module.scss'

/**
 * BaseTable - A foundational table component that handles common table patterns
 *
 * @param {Object} props - Component props
 * @param {Array} props.columns - Array of column definitions with { id, label, align, sortable, renderCell }
 * @param {Array} props.data - Array of data objects to display in the table
 * @param {boolean} props.loading - Whether the table is loading
 * @param {boolean} props.searched - Whether a search has been performed (for CallResultsTable)
 * @param {string} props.sortField - The current sort field
 * @param {string} props.sortDirection - The current sort direction ('asc' or 'desc')
 * @param {Function} props.onSort - Callback for sorting columns
 * @param {Function} props.onRowClick - Callback for row click
 * @param {Object} props.emptyState - Configuration for empty state
 * @param {Object} props.loadingState - Configuration for loading state
 * @param {number} props.colSpan - Number of columns for empty/loading states
 * @param {Object} props.containerProps - Additional props for the TableContainer
 * @param {Object} props.tableProps - Additional props for the Table component
 */
const BaseTable = ({
  columns = [],
  data = [],
  loading = false,
  searched = false,
  sortField = null,
  sortDirection = 'asc',
  onSort = () => {},
  onRowClick = () => {},
  emptyState = null,
  loadingState = null,
  colSpan = 1,
  containerProps = {},
  tableProps = {},
  renderHeaderContent = null,
  rowHeaderColumn = 0
}) => {
  const containerRef = useRef(null)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    setCanScrollRight(el.scrollWidth - el.scrollLeft - el.clientWidth > 2)
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return undefined
    checkScroll()
    el.addEventListener('scroll', checkScroll, { passive: true })
    const ro = new ResizeObserver(checkScroll)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', checkScroll)
      ro.disconnect()
    }
  }, [checkScroll, data])

  const renderHeaderCell = (column, index) => {
    const cellClasses = [styles.tableHeaderCell]
    if (column.sortable) {
      cellClasses.push(styles.sortableHeaderCell)
    }
    if (column.columnClassName) {
      cellClasses.push(column.columnClassName)
    }

    const { title: headerTitle, ...restHeaderCellProps } = column.headerCellProps || {}

    const isCurrentlySorted = column.sortable && sortField === column.id
    const ariaSortValue = isCurrentlySorted
      ? (sortDirection === 'asc' ? 'ascending' : 'descending')
      : undefined

    const defaultSortIndicator = isCurrentlySorted
      ? <span className={styles.sortArrow}>{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>
      : column.sortable ? <span className={styles.sortArrowInactive}> ▲</span> : null

    const cellContent = (
      <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 'fit-content', cursor: column.sortable ? 'pointer' : 'default' }}>
        {column.label}
        {column.sortable && (column.renderSortIcon ? column.renderSortIcon(sortDirection) : defaultSortIndicator)}
      </span>
    )

    return (
      <TableCell
        key={column.id || index}
        className={cellClasses.join(' ')}
        align={column.align || 'left'}
        onClick={column.sortable ? () => onSort(column.id) : undefined}
        style={column.style}
        aria-sort={ariaSortValue}
        {...restHeaderCellProps}
      >
        {headerTitle ? (
          <Tooltip title={headerTitle} arrow placement="top" describeChild>
            {cellContent}
          </Tooltip>
        ) : cellContent}
      </TableCell>
    )
  }

  const getCellClassName = (column) => {
    const base = column.cellClassName || styles.tableCell
    return column.columnClassName ? `${base} ${column.columnClassName}` : base
  }

  const renderDataCell = (row, column, rowIndex, columnIndex) => {
    const combinedStyles = {
      ...(column.style || {}),
      ...(column.cellStyle || {})
    }

    const content = column.renderCell
      ? column.renderCell(row, rowIndex)
      : row[column.id]

    const needsTooltip = typeof content === 'string' || typeof content === 'number'
    const isRowHeader = rowHeaderColumn != null && columnIndex === rowHeaderColumn

    return (
      <TableCell
        key={column.id || columnIndex}
        className={getCellClassName(column)}
        align={column.align || 'left'}
        style={combinedStyles}
        title={needsTooltip ? String(content) : ''}
        {...(isRowHeader ? { component: 'th', scope: 'row' } : {})}
        {...column.cellProps}
      >
        {content}
      </TableCell>
    )
  }

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableBody>
          {loadingState || <TableLoadingState colSpan={colSpan || columns.length} rows={5} />}
        </TableBody>
      )
    }

    if (!data.length) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={colSpan || columns.length} className={styles.tableCell}>
              {emptyState || (
                <TableEmptyState searched={searched} />
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      )
    }

    return (
      <TableBody>
        {data.map((row, rowIndex) => (
          <TableRow
            key={`row-${rowIndex}-${row.id || ''}`}
            className={styles.tableRow}
            onClick={() => onRowClick(row, rowIndex)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onRowClick(row, rowIndex)
              }
            }}
            tabIndex={0}
            role='row'
            hover
          >
            {columns.map((column, columnIndex) =>
              renderDataCell(row, column, rowIndex, columnIndex)
            )}
          </TableRow>
        ))}
      </TableBody>
    )
  }

  return (
    <div className={styles.scrollWrapper}>
      <div
        className={`${styles.scrollFade} ${canScrollRight ? styles.scrollFadeVisible : ''}`}
      />
      <TableContainer
        component={Paper}
        className={styles.tableContainer}
        ref={containerRef}
        {...containerProps}
      >
        {renderHeaderContent && renderHeaderContent()}

        <Table className={styles.table} {...tableProps}>
          <TableHead>
            <TableRow>
              {columns.map(renderHeaderCell)}
            </TableRow>
          </TableHead>
          {renderTableContent()}
        </Table>
      </TableContainer>
    </div>
  )
}

BaseTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.node,
    align: PropTypes.oneOf(['left', 'center', 'right']),
    sortable: PropTypes.bool,
    renderCell: PropTypes.func,
    renderSortIcon: PropTypes.func,
    cellClassName: PropTypes.string,
    columnClassName: PropTypes.string,
    style: PropTypes.object,
    cellStyle: PropTypes.object,
    headerCellProps: PropTypes.object,
    cellProps: PropTypes.object
  })),
  data: PropTypes.array,
  loading: PropTypes.bool,
  searched: PropTypes.bool,
  sortField: PropTypes.string,
  sortDirection: PropTypes.string,
  onSort: PropTypes.func,
  onRowClick: PropTypes.func,
  emptyState: PropTypes.node,
  loadingState: PropTypes.node,
  colSpan: PropTypes.number,
  containerProps: PropTypes.object,
  tableProps: PropTypes.object,
  renderHeaderContent: PropTypes.func,
  rowHeaderColumn: PropTypes.number
}

export default BaseTable
